/**
 * One vended serial, rendered as a Cloudscape Container: the drawn barcode, the human-readable
 * PIC grouped in 4s, and the parsed field breakdown as a KeyValuePairs grid.
 *
 * The human-readable string is produced by impb.js (`groupDigits`) and is NOT re-derived here —
 * the grouping rule and the 420+ZIP suppression rule are spec behaviour, so they live in the
 * one module that has 71/71 conformance tests against the spec.
 */

import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Alert from '@cloudscape-design/components/alert';
import BarcodeCanvas from './BarcodeCanvas.jsx';
import { Impb } from '../lib/impb.js';

export default function SerialCard({ barcode, index, mode, extra = {} }) {
  const spec = Impb.CONSTRUCTS[barcode.construct] || {};
  const suppressed = Boolean(barcode.fields && barcode.fields.routingAI);

  const pairs = [
    { label: 'PIC (tracking number)', value: mono(barcode.pic) },
    { label: 'Encoded digits (in the bars)', value: mono(barcode.encoded) },
    { label: 'Serial', value: mono(barcode.fields.serial) },
    { label: 'Mailer ID (fabricated)', value: mono(barcode.fields.mailerId) },
    { label: 'Service Type Code', value: mono(barcode.fields.serviceTypeCode) },
    { label: 'MOD 10 data check digit', value: mono(barcode.fields.checkDigit) },
  ];
  if (suppressed) {
    pairs.push({
      label: 'Routing AI + ZIP (encoded, not printed)',
      value: mono(`${barcode.fields.routingAI} ${barcode.fields.zip}`),
    });
  }
  if (spec.description) pairs.push({ label: 'Construct', value: spec.description });
  if (extra.regionId) pairs.push({ label: 'Region partition', value: mono(extra.regionId) });
  if (extra.requestId) pairs.push({ label: 'API request id', value: mono(extra.requestId) });

  return (
    <Container
      header={
        <Header
          variant="h3"
          actions={
            <Badge color={extra.live ? 'green' : 'grey'}>{extra.live ? 'LIVE' : 'LOCAL RENDER'}</Badge>
          }
        >
          {index === null ? 'Label' : `Serial #${index + 1}`} — construct {barcode.construct}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {barcode.inferredConstruct ? (
          <Alert type="info" header="Construct was INFERRED, not declared">
            The response did not declare a construct, so this label comes from{' '}
            <code>impb.parseBarcode()</code>. C01, C03 and C04 share a byte-identical 22-digit PIC
            signature, so a bare PIC cannot always identify itself — treat the construct shown as a
            best guess, not a fact.
          </Alert>
        ) : null}

        {barcode.hrMismatch ? (
          <Alert type="error" header="Contract mismatch — API human-readable disagrees with the spec">
            API returned <code>{barcode.hrMismatch.api}</code>; <code>shared/impb</code> grouping
            says <code>{barcode.hrMismatch.expected}</code>. Surfaced rather than hidden: this is a
            real integration bug.
          </Alert>
        ) : null}

        <BarcodeCanvas barcode={barcode} mode={mode} />

        <div>
          <Box
            variant="h2"
            fontSize="display-l"
            fontWeight="bold"
            data-testid="human-readable-pic"
          >
            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.04em' }}>
              {barcode.humanReadable}
            </span>
          </Box>
          <Box variant="small" color="text-body-secondary">
            Human-readable PIC, 4-digit groups per Spec Rev E §8.5.{' '}
            {suppressed ? (
              <>
                The <code>420</code>+ZIP routing AI is <strong>suppressed from the printed text</strong>{' '}
                but present in the bars — {barcode.encoded.length} digits encoded,{' '}
                {barcode.pic.length} digits shown.
              </>
            ) : (
              <>This construct carries no routing element, so encoded and printed digits match.</>
            )}
          </Box>
        </div>

        <KeyValuePairs columns={3} items={pairs} />
      </SpaceBetween>
    </Container>
  );
}

const mono = (v) => (
  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
    {String(v == null ? '—' : v)}
  </span>
);
